import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowRight,
  BellRing,
  CalendarCheck,
  CalendarClock,
  Check,
  ChevronDown,
  CircleAlert,
  Clock3,
  LayoutDashboard,
  MapPin,
  Plus,
  ShieldCheck,
  Users,
  Wrench,
} from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { PhoneInput } from '../../components/ui/phone-input';
import { createPublicAuditRequest } from '../../lib/api/public-marketing-client';
import { hasPhoneValue, normalizePhoneValue } from '../../lib/phone';
import { cn } from '../../lib/utils';
import { BRAND_NAME, buildPublicAssetPath } from '../../lib/brand';
import {
  LOGIN_PATH,
  resolveAuthenticatedPath,
} from '../auth/auth-routing';
import { useAuth } from '../auth/auth-context';
import { LANDING_FAQ_ENTRIES } from './landing-seo';

const WRAP_CLASS_NAME = 'mx-auto w-full max-w-[1080px] px-6 sm:px-7';

const NAV_LOGO_SRC = buildPublicAssetPath('/belahora-lockup-nav.png');

const LEGAL_LINKS = [
  { href: '/privacidade', label: 'Política de Privacidade' },
  { href: '/cookies', label: 'Política de Cookies' },
  { href: '/termos', label: 'Termos e Condições' },
] as const;

const FIELD_CLASS_NAME =
  'h-14 rounded-2xl border-[#1c4f55]/20 bg-white text-[#0f2e32] shadow-[0_10px_25px_rgba(28,79,85,0.06)] placeholder:text-[#9aa6a3] focus-visible:ring-[#1c4f55]/25 focus-visible:ring-offset-white';
const LABEL_CLASS_NAME =
  'block text-[11px] font-medium uppercase tracking-[0.18em] text-[#5a6e6b]';

const SMS_OPTIONS = [
  { value: 1, title: '1 SMS', detail: 'Lembrete 24h antes' },
  { value: 2, title: '2 SMS', detail: '24h + 1h antes' },
  { value: 3, title: '3 SMS', detail: 'Confirmação + 24h + 1h' },
  { value: 4, title: '4 SMS', detail: '3 lembretes + aniversário' },
] as const;

const VALUE_CARDS = [
  {
    icon: BellRing,
    title: 'Comunicações automáticas anti-faltas',
    description:
      'Lembretes por SMS e email antes de cada marcação. O cliente confirma. A falta não acontece. A receita fica no teu bolso.',
    badge: 'O coração do sistema — reduz faltas em até 80%',
  },
  {
    icon: CalendarClock,
    title: 'Agenda digital — marcas tu ou o cliente',
    description:
      'Podes abrir a página pública para os clientes marcarem sozinhos, ou manter a agenda privada e marcar tu. De qualquer forma, os lembretes funcionam.',
    badge: 'Flexível — funciona para qualquer modelo de negócio',
  },
  {
    icon: Wrench,
    title: 'Setup completo em 48h',
    description:
      'Configuramos nós. Serviços, equipa, horários, lembretes, página pública. Tu só validas.',
    badge: 'Incluído — sem te deixarmos sozinho',
  },
  {
    icon: LayoutDashboard,
    title: 'Painel de leitura do dia',
    description:
      'Vês as marcações de hoje, as faltas evitadas, as mensagens poupadas. Foco no que importa.',
    badge: 'Incluído — clareza diária',
  },
  {
    icon: Users,
    title: 'Acompanhamento local',
    description:
      'Não tens suporte genérico. Tens alguém nos Açores que conhece o mercado e o teu negócio.',
    badge: 'Incluído — resposta com contexto',
  },
  {
    icon: ShieldCheck,
    title: 'RGPD e consentimento',
    description:
      'Registo de contacto com consentimento automático. Dados protegidos. Campanhas futuras legais.',
    badge: 'Incluído — conformidade desde o início',
  },
] as const;

const PROOF_NUMBERS = [
  { value: '−80%', label: 'redução de faltas no primeiro mês' },
  { value: '48h', label: 'do pedido ao sistema ativo' },
  { value: '+240€', label: 'recuperados em média por mês' },
  { value: '0', label: 'configurações feitas pelo dono' },
] as const;

const PROCESS_STEPS = [
  {
    title: 'Auditoria gratuita',
    description:
      'Percebemos onde está o desperdício. Marcações, faltas, processo — lemos o teu caso com contexto real.',
  },
  {
    title: 'Setup em 48h',
    description:
      'Configuramos serviços, horários, equipa, lembretes e página. Tu validas. Nós ativamos.',
  },
  {
    title: 'Faltas evitadas. Receita protegida.',
    description:
      'Lembretes a correr. Faltas a cair. No Dia 30 mostramos-te o dinheiro que recuperaste — em números concretos.',
  },
] as const;

const PRICE_FEATURES = [
  'Página de marcação pública ativa',
  'Lembretes anti-faltas configurados',
  'Setup completo feito por nós',
  'Painel de leitura diária',
  'Acompanhamento local nos Açores',
  'RGPD e consentimento incluídos',
  'Cancelamento simples, sem burocracia',
] as const;

const LOCAL_BADGES = [
  'Mercado pequeno, reputação rápida',
  'Apoio em português açoriano',
  'Contexto local real',
  'Sem suporte genérico',
] as const;

const ISLANDS = [
  'São Miguel',
  'Terceira',
  'Faial',
  'Pico',
  'São Jorge',
  'Graciosa',
  'Santa Maria',
  'Flores',
  'Corvo',
] as const;

const ACTIVE_ISLANDS = new Set<string>([
  'São Miguel',
  'Terceira',
  'Faial',
  'Pico',
]);

const SMS_PLANS = [
  { name: 'Essencial', price: 29, sms: 200 },
  { name: 'Crescimento', price: 59, sms: 800 },
  { name: 'Premium', price: 99, sms: 1500 },
] as const;

const WEEKS_PER_MONTH = 4;
const EXTRA_PACK_SMS = 200;
const EXTRA_PACK_PRICE = 9;

const auditSchema = z.object({
  ownerName: z
    .string()
    .trim()
    .min(1, 'Indica o teu nome.')
    .max(120, 'Usa um nome mais curto.'),
  businessName: z
    .string()
    .trim()
    .min(1, 'Indica o nome do salão.')
    .max(120, 'Usa um nome mais curto.'),
  phoneOrWhatsapp: z
    .string()
    .trim()
    .min(1, 'Indica um telefone ou WhatsApp.')
    .max(60, 'Usa um contacto mais curto.')
    .refine((value) => hasPhoneValue(value), {
      message: 'Indica um telefone ou WhatsApp.',
    }),
  island: z.string().trim().min(1, 'Escolhe a ilha.'),
  email: z
    .string()
    .trim()
    .refine((value) => !value || z.string().email().safeParse(value).success, {
      message: 'Introduz um email válido ou deixa em branco.',
    }),
  instagramHandle: z.string().trim().max(120, 'Usa um identificador mais curto.'),
  consent: z.boolean().refine((value) => value, {
    message:
      'Precisamos da tua autorização para responder ao pedido de auditoria.',
  }),
});

type AuditFormValues = z.infer<typeof auditSchema>;

interface CalculatorState {
  avgTicket: number;
  clientsPerWeek: number;
  noShowRate: number;
}

interface AuditContextState {
  estimatedMonthlyLossEur: number | null;
  source: 'landing_audit_form' | 'landing_calculator';
}

interface SmsPlanRecommendation {
  bookingsPerMonth: number;
  smsPerMonth: number;
  planName: string;
  basePrice: number;
  extraPacks: number;
  totalPrice: number;
  leftoverSms: number;
}

function roundCurrency(value: number) {
  return Math.round(value);
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

function calculateLosses({ avgTicket, clientsPerWeek, noShowRate }: CalculatorState) {
  const safeClientsPerWeek = Math.max(0, clientsPerWeek);
  const safeAvgTicket = Math.max(0, avgTicket);
  const safeNoShowRate = Math.max(0, Math.min(100, noShowRate));
  const monthlyLoss = roundCurrency(
    safeClientsPerWeek * safeAvgTicket * (safeNoShowRate / 100) * WEEKS_PER_MONTH
  );
  const yearlyLoss = roundCurrency(monthlyLoss * 12);
  const recoverableMonthly = roundCurrency(monthlyLoss * 0.8);

  return {
    monthlyLoss,
    recoverableMonthly,
    yearlyLoss,
  };
}

function recommendSmsPlan(
  clientsPerWeek: number,
  smsPerBooking: number
): SmsPlanRecommendation {
  const bookingsPerMonth = Math.round(
    Math.max(0, clientsPerWeek) * WEEKS_PER_MONTH
  );
  const smsPerMonth = bookingsPerMonth * smsPerBooking;
  const covering = SMS_PLANS.find((plan) => plan.sms >= smsPerMonth);
  const plan = covering ?? SMS_PLANS[SMS_PLANS.length - 1];
  const extraPacks = covering
    ? 0
    : Math.ceil((smsPerMonth - plan.sms) / EXTRA_PACK_SMS);
  const totalPrice = plan.price + extraPacks * EXTRA_PACK_PRICE;
  const leftoverSms = plan.sms + extraPacks * EXTRA_PACK_SMS - smsPerMonth;

  return {
    bookingsPerMonth,
    smsPerMonth,
    planName: plan.name,
    basePrice: plan.price,
    extraPacks,
    totalPrice,
    leftoverSms,
  };
}

function SectionTag({
  children,
  tone = 'teal',
}: {
  children: React.ReactNode;
  tone?: 'teal' | 'amber';
}) {
  return (
    <span
      className={cn(
        'mb-3 block text-[10px] font-medium uppercase tracking-[0.25em]',
        tone === 'teal' ? 'text-[#1c4f55]' : 'text-[#fad37a]'
      )}
    >
      {children}
    </span>
  );
}

function FieldMessage({ message, id }: { id: string; message?: string }) {
  if (!message) {
    return null;
  }

  return (
    <p id={id} className="px-2 text-sm font-medium text-destructive">
      {message}
    </p>
  );
}

function SelectField(
  props: React.SelectHTMLAttributes<HTMLSelectElement> & {
    id: string;
    describedBy?: string;
  }
) {
  const { className, describedBy, ...rest } = props;

  return (
    <div className="relative">
      <select
        aria-describedby={describedBy}
        className={cn(
          'h-14 w-full appearance-none rounded-2xl border border-[#1c4f55]/20 bg-white px-5 pr-12 text-base text-[#0f2e32] shadow-[0_10px_25px_rgba(28,79,85,0.06)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1c4f55]/25 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:bg-[#f1eadd] disabled:text-[#9aa6a3]',
          className
        )}
        {...rest}
      />
      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#5a6e6b]" />
    </div>
  );
}

export function LandingPage() {
  const { isAuthenticated, isHydrating, session } = useAuth();
  const location = useLocation();
  const [calculator, setCalculator] = React.useState<CalculatorState>({
    clientsPerWeek: 30,
    avgTicket: 25,
    noShowRate: 10,
  });
  const [smsPerBooking, setSmsPerBooking] = React.useState(2);
  const [auditContext, setAuditContext] = React.useState<AuditContextState>({
    estimatedMonthlyLossEur: null,
    source: 'landing_audit_form',
  });
  const [submissionState, setSubmissionState] = React.useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const [submissionError, setSubmissionError] = React.useState<string | null>(null);

  const calculatorResults = React.useMemo(
    () => calculateLosses(calculator),
    [calculator]
  );

  const planRecommendation = React.useMemo(
    () => recommendSmsPlan(calculator.clientsPerWeek, smsPerBooking),
    [calculator.clientsPerWeek, smsPerBooking]
  );

  const planLabel =
    planRecommendation.extraPacks > 0
      ? `${planRecommendation.planName} + ${planRecommendation.extraPacks} pack${
          planRecommendation.extraPacks > 1 ? 's' : ''
        } — ${planRecommendation.totalPrice}€/mês`
      : `${planRecommendation.planName} — ${planRecommendation.basePrice}€/mês`;

  const planHint =
    planRecommendation.leftoverSms >= 0
      ? `Com ${planRecommendation.bookingsPerMonth} marcações/mês e ${smsPerBooking} SMS cada, o plano ${planRecommendation.planName} cobre-te com ${planRecommendation.leftoverSms} SMS de margem. O sistema paga-se com a primeira falta evitada.`
      : `Com ${planRecommendation.bookingsPerMonth} marcações/mês precisas de packs adicionais. Para negócios com este volume temos uma proposta personalizada — fala connosco.`;

  const utmParams = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    return {
      utm_source: searchParams.get('utm_source'),
      utm_medium: searchParams.get('utm_medium'),
      utm_campaign: searchParams.get('utm_campaign'),
    };
  }, [location.search]);

  const appAction =
    !isHydrating && isAuthenticated && session
      ? {
          href: resolveAuthenticatedPath(session),
          label: session.hasBusiness ? 'Abrir app' : 'Continuar setup',
        }
      : {
          href: LOGIN_PATH,
          label: 'Entrar',
        };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<AuditFormValues>({
    resolver: zodResolver(auditSchema),
    mode: 'onBlur',
    defaultValues: {
      ownerName: '',
      businessName: '',
      phoneOrWhatsapp: '',
      island: '',
      email: '',
      instagramHandle: '',
      consent: false,
    },
  });

  function updateCalculatorField(field: keyof CalculatorState, value: string) {
    setCalculator((current) => ({
      ...current,
      [field]: Number(value) || 0,
    }));
  }

  function scrollToSection(sectionId: string) {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  React.useEffect(() => {
    const target = location.hash.replace('#', '');

    if (target) {
      scrollToSection(target);
    }
  }, [location.hash]);

  function handleAnchorClick(
    event: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ) {
    event.preventDefault();
    scrollToSection(sectionId);
  }

  function handlePrimaryAuditClick(event?: React.MouseEvent<HTMLElement>) {
    event?.preventDefault();
    setAuditContext({
      estimatedMonthlyLossEur: null,
      source: 'landing_audit_form',
    });
    setSubmissionState('idle');
    setSubmissionError(null);
    scrollToSection('auditoria');
  }

  function handleCalculatorNavigationClick(
    event: React.MouseEvent<HTMLAnchorElement>
  ) {
    event.preventDefault();
    scrollToSection('calculadora');
  }

  function handleCalculatorAuditClick() {
    setAuditContext({
      estimatedMonthlyLossEur: calculatorResults.monthlyLoss,
      source: 'landing_calculator',
    });
    setSubmissionState('idle');
    setSubmissionError(null);
    scrollToSection('auditoria');
  }

  async function onSubmit(values: AuditFormValues) {
    setSubmissionState('submitting');
    setSubmissionError(null);

    try {
      await createPublicAuditRequest({
        owner_name: values.ownerName.trim(),
        business_name: values.businessName.trim(),
        phone_or_whatsapp:
          normalizePhoneValue(values.phoneOrWhatsapp) ?? values.phoneOrWhatsapp.trim(),
        island: values.island.trim(),
        email: values.email.trim() || null,
        instagram_handle: values.instagramHandle.trim() || null,
        estimated_monthly_loss_eur: auditContext.estimatedMonthlyLossEur,
        source: auditContext.source,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
      });

      setSubmissionState('success');
      reset({
        ownerName: '',
        businessName: '',
        phoneOrWhatsapp: '',
        island: '',
        email: '',
        instagramHandle: '',
        consent: false,
      });
    } catch (error) {
      setSubmissionState('error');
      setSubmissionError(
        error instanceof Error
          ? error.message
          : 'Não foi possível enviar o pedido agora.'
      );
    }
  }

  return (
    <div className="min-h-screen bg-[#f1eadd] font-body text-[#0f2e32]">
      <header className="sticky top-0 z-50 border-b border-[#1c4f55]/10 bg-[#f1eadd]/90 py-2.5 backdrop-blur-xl">
        <div className={cn(WRAP_CLASS_NAME, 'flex items-center justify-between')}>
          <a
            href="#top"
            onClick={(event) => handleAnchorClick(event, 'top')}
            aria-label={BRAND_NAME}
          >
            <img
              src={NAV_LOGO_SRC}
              alt={BRAND_NAME}
              width={920}
              height={833}
              decoding="async"
              className="h-16 w-auto object-contain"
            />
          </a>

          <nav className="flex items-center gap-5 sm:gap-7" aria-label="Principal">
            <a
              href="#como-funciona"
              onClick={(event) => handleAnchorClick(event, 'como-funciona')}
              className="hidden text-sm text-[#5a6e6b] transition-colors hover:text-[#1c4f55] sm:inline"
            >
              Como funciona
            </a>
            <a
              href="#precos"
              onClick={(event) => handleAnchorClick(event, 'precos')}
              className="hidden text-sm text-[#5a6e6b] transition-colors hover:text-[#1c4f55] sm:inline"
            >
              Preços
            </a>
            <Button
              asChild
              size="sm"
              className="bg-[#1c4f55] text-white hover:bg-[#2a6b74]"
            >
              <Link to={appAction.href}>{appAction.label}</Link>
            </Button>
          </nav>
        </div>
      </header>

      <main id="top">
        {/* HERO */}
        <section className={cn(WRAP_CLASS_NAME, 'py-12 sm:py-16')}>
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-14">
            <div>
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#1c4f55]/20 bg-[#1c4f55]/[0.06] px-3 py-1.5 text-xs font-medium text-[#1c4f55]">
                <span className="h-2 w-2 rounded-full bg-[#f5b731]" />
                Nos Açores, a operar de perto
              </span>
              <h1 className="font-display text-[clamp(2.5rem,6vw,3.4rem)] font-bold leading-[1.05] tracking-tight">
                Cada falta que evitas{' '}
                <em className="italic text-[#1c4f55]">paga o sistema.</em>
              </h1>
              <p className="mt-5 max-w-[26rem] text-base leading-relaxed text-[#5a6e6b]">
                Os salões perdem em média 10% da receita mensal em faltas. O
                BelaHora envia comunicações automáticas que reduzem esse número
                para quase zero — e paga-se com a primeira falta evitada.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button
                  asChild
                  size="lg"
                  className="h-auto min-h-14 w-full whitespace-normal py-3 leading-snug bg-[#f5b731] text-[#0f2e32] shadow-[0_6px_20px_rgba(245,183,49,0.35)] hover:bg-[#f0ad1f] hover:shadow-[0_8px_24px_rgba(245,183,49,0.45)] sm:w-auto"
                >
                  <a href="#calculadora" onClick={handleCalculatorNavigationClick}>
                    Calcular o que estás a perder
                    <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="h-auto min-h-14 w-full whitespace-normal py-3 leading-snug border-[#1c4f55]/25 bg-transparent text-[#0f2e32] hover:border-[#1c4f55] hover:bg-[#1c4f55]/5 sm:w-auto"
                >
                  <a href="#auditoria" onClick={handlePrimaryAuditClick}>
                    Pedir auditoria gratuita
                  </a>
                </Button>
              </div>

              <div className="mt-9 flex flex-wrap gap-6">
                {['Desde 29€/mês', 'Setup em 48h', 'Paga-se ou 2.º mês grátis'].map(
                  (item) => (
                    <span
                      key={item}
                      className="flex items-center gap-2 text-xs text-[#5a6e6b]"
                    >
                      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#1c4f55] text-white">
                        <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      </span>
                      {item}
                    </span>
                  )
                )}
              </div>
            </div>

            {/* Hero visual */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-[1.4rem] bg-[#1c4f55] p-7 shadow-[0_28px_60px_rgba(15,46,50,0.28)]">
                <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-[#f5b731]/10" />
                <div className="relative">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                    Hoje no teu salão
                  </p>
                  <p className="mt-3 font-display text-[3.25rem] font-bold leading-none text-[#fad37a]">
                    {formatCurrency(calculatorResults.monthlyLoss)}
                  </p>
                  <p className="mt-1 text-[13px] text-white/55">
                    perdidos por mês em faltas
                  </p>

                  <div className="mt-5 grid grid-cols-2 gap-2.5">
                    <div className="rounded-xl bg-white/10 px-3.5 py-3">
                      <p className="text-xl font-medium text-white">3</p>
                      <p className="text-[11px] text-white/50">faltas evitadas hoje</p>
                    </div>
                    <div className="rounded-xl bg-white/10 px-3.5 py-3">
                      <p className="text-xl font-medium text-white">€75</p>
                      <p className="text-[11px] text-white/50">
                        receita protegida hoje
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2.5 rounded-xl bg-white px-3.5 py-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#1c4f55]/[0.08] text-[#1c4f55]">
                      <BellRing className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-[13px] font-medium text-[#0f2e32]">
                        Lembrete enviado · Ana Silva
                      </p>
                      <p className="text-[11px] text-[#5a6e6b]">
                        Confirmou para amanhã às 10h · agora mesmo
                      </p>
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-2.5 rounded-xl bg-white px-3.5 py-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#1c4f55]/[0.08] text-[#1c4f55]">
                      <CalendarCheck className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-[13px] font-medium text-[#0f2e32]">
                        Nova marcação · Corte + Barba
                      </p>
                      <p className="text-[11px] text-[#5a6e6b]">
                        Sábado 10:00 · sem chamada nenhuma
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CALCULADORA */}
        <section className={WRAP_CLASS_NAME}>
          <div
            id="calculadora"
            className="scroll-mt-28 rounded-[1.75rem] bg-[#1c4f55] p-6 sm:p-12"
          >
            <div className="mx-auto mb-10 max-w-[30rem] text-center">
              <span className="mb-4 inline-block rounded-full border border-[#f5b731]/30 bg-[#f5b731]/[0.18] px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#fad37a]">
                Calculadora de receita perdida
              </span>
              <h2 className="font-display text-[clamp(1.9rem,4vw,2.25rem)] font-bold leading-tight tracking-tight text-white">
                Quanto perdes por mês em faltas — e qual o plano certo.
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-white/55">
                Dois passos: primeiro vês o dinheiro que estás a perder. Depois o
                sistema recomenda o plano de comunicações que o recupera.
              </p>
            </div>

            <div className="grid gap-7 lg:grid-cols-2">
              {/* Inputs */}
              <div className="rounded-2xl bg-white/[0.07] p-6 sm:p-7">
                <p className="mb-5 text-[10px] uppercase tracking-[0.18em] text-white/40">
                  Passo 1 — Quanto perdes
                </p>

                <div className="space-y-6">
                  <div>
                    <label
                      htmlFor="calc-clients"
                      className="mb-3 block text-[10px] uppercase tracking-[0.18em] text-white/40"
                    >
                      Clientes por semana
                    </label>
                    <div className="flex items-center gap-3.5">
                      <input
                        id="calc-clients"
                        type="range"
                        min={5}
                        max={150}
                        step={1}
                        value={calculator.clientsPerWeek}
                        onChange={(event) =>
                          updateCalculatorField('clientsPerWeek', event.target.value)
                        }
                        className="belahora-range flex-1"
                      />
                      <span className="min-w-[3.25rem] text-right text-lg font-medium text-white">
                        {calculator.clientsPerWeek}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="calc-price"
                      className="mb-3 block text-[10px] uppercase tracking-[0.18em] text-white/40"
                    >
                      Preço médio por serviço (€)
                    </label>
                    <div className="flex items-center gap-3.5">
                      <input
                        id="calc-price"
                        type="range"
                        min={10}
                        max={150}
                        step={5}
                        value={calculator.avgTicket}
                        onChange={(event) =>
                          updateCalculatorField('avgTicket', event.target.value)
                        }
                        className="belahora-range flex-1"
                      />
                      <span className="min-w-[3.25rem] text-right text-lg font-medium text-white">
                        {calculator.avgTicket}€
                      </span>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="calc-noshow"
                      className="mb-3 block text-[10px] uppercase tracking-[0.18em] text-white/40"
                    >
                      Taxa estimada de faltas (%)
                    </label>
                    <div className="flex items-center gap-3.5">
                      <input
                        id="calc-noshow"
                        type="range"
                        min={2}
                        max={30}
                        step={1}
                        value={calculator.noShowRate}
                        onChange={(event) =>
                          updateCalculatorField('noShowRate', event.target.value)
                        }
                        className="belahora-range flex-1"
                      />
                      <span className="min-w-[3.25rem] text-right text-lg font-medium text-white">
                        {calculator.noShowRate}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-5 border-t border-white/10 pt-5">
                  <p className="mb-3 text-[10px] uppercase tracking-[0.18em] text-white/40">
                    Passo 2 — SMS por marcação (tu escolhes)
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {SMS_OPTIONS.map((option) => {
                      const isActive = option.value === smsPerBooking;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={isActive}
                          onClick={() => setSmsPerBooking(option.value)}
                          className={cn(
                            'rounded-[0.65rem] border p-2.5 text-center text-xs transition-colors',
                            isActive
                              ? 'border-[#f5b731]/40 bg-[#f5b731]/[0.18] text-white/80'
                              : 'border-white/10 bg-white/[0.07] text-white/60 hover:border-white/25'
                          )}
                        >
                          <span className="mb-0.5 block text-lg font-medium text-white">
                            {option.title}
                          </span>
                          {option.detail}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="flex flex-col gap-3.5">
                <div className="rounded-2xl bg-[#f5b731] p-6 sm:p-7">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#0f2e32]/55">
                    Perda mensal em faltas
                  </p>
                  <p className="mt-2 font-display text-[3.25rem] font-bold leading-none text-[#0f2e32]">
                    {formatCurrency(calculatorResults.monthlyLoss)}
                  </p>
                  <p className="mt-1 text-[13px] text-[#0f2e32]/60">
                    por mês em faltas não evitadas
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <div className="rounded-xl bg-white/[0.08] p-4">
                    <p className="mb-1 text-[11px] text-white/45">Perda anual</p>
                    <p className="text-xl font-medium text-white">
                      {formatCurrency(calculatorResults.yearlyLoss)}
                    </p>
                  </div>
                  <div className="rounded-xl bg-white/[0.08] p-4">
                    <p className="mb-1 text-[11px] text-white/45">
                      Recuperável c/ BelaHora
                    </p>
                    <p className="text-xl font-medium text-white">
                      {formatCurrency(calculatorResults.recoverableMonthly)}
                    </p>
                  </div>
                </div>

                <div className="rounded-xl bg-white/[0.08] p-4">
                  <p className="mb-1 text-[11px] text-white/45">
                    Plano recomendado para ti
                  </p>
                  <p className="text-base font-medium text-white">{planLabel}</p>
                </div>

                <p className="rounded-xl bg-white/[0.06] px-4 py-3.5 text-[13px] leading-relaxed text-white/50">
                  {planHint}
                </p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Button
                type="button"
                onClick={handleCalculatorAuditClick}
                size="lg"
                className="h-auto min-h-14 w-full whitespace-normal py-3 leading-snug bg-white text-[#1c4f55] hover:bg-white/90 sm:w-auto"
              >
                Usar estes valores na auditoria gratuita
                <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
              </Button>
              <p className="mt-3 text-xs text-white/35">
                Sem compromisso. Usamos estes valores para perceber o teu caso
                antes de qualquer conversa.
              </p>
            </div>
          </div>
        </section>

        {/* VALUE STACK */}
        <section className={cn(WRAP_CLASS_NAME, 'py-16 sm:py-20')}>
          <SectionTag>O que recebes</SectionTag>
          <h2 className="font-display text-[clamp(1.9rem,4vw,2.5rem)] font-bold leading-tight tracking-tight">
            Não vendemos software.{' '}
            <em className="italic text-[#1c4f55]">Vendemos receita protegida.</em>
          </h2>
          <p className="mt-3 max-w-[33rem] text-[15px] leading-relaxed text-[#5a6e6b]">
            Cada componente tem um valor concreto. Juntos, mudam a forma como o teu
            negócio opera.
          </p>

          <div className="mt-11 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {VALUE_CARDS.map(({ icon: Icon, title, description, badge }) => (
              <div
                key={title}
                className="rounded-[1.1rem] border border-[#1c4f55]/[0.13] bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(28,79,85,0.1)]"
              >
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[#1c4f55]/[0.07] text-[#1c4f55]">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mb-2 text-[15px] font-medium text-[#0f2e32]">
                  {title}
                </h3>
                <p className="mb-3.5 text-[13px] leading-relaxed text-[#5a6e6b]">
                  {description}
                </p>
                <p className="border-t border-[#1c4f55]/[0.13] pt-3 text-[11px] font-medium text-[#1c4f55]">
                  {badge}
                </p>
              </div>
            ))}
          </div>
        </section>

        <div className="mx-auto h-px max-w-[1080px] bg-[#1c4f55]/[0.13]" />

        {/* PROOF */}
        <section className={cn(WRAP_CLASS_NAME, 'py-16 sm:py-20')}>
          <SectionTag>Resultados reais</SectionTag>
          <h2 className="font-display text-[clamp(1.9rem,4vw,2.5rem)] font-bold leading-tight tracking-tight">
            Números de salões que já arrancaram.
          </h2>

          <div className="mt-11 grid items-start gap-8 lg:grid-cols-2">
            <div className="grid grid-cols-2 gap-3.5">
              {PROOF_NUMBERS.map((item) => (
                <div key={item.label} className="rounded-2xl bg-[#1c4f55] p-5 sm:p-6">
                  <p className="font-display text-[2.25rem] font-bold leading-none text-[#fad37a]">
                    {item.value}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-white/50">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>

            <figure className="rounded-[1.25rem] border border-[#1c4f55]/[0.13] bg-white p-7">
              <blockquote className="font-display text-lg italic leading-relaxed text-[#0f2e32]">
                {
                  '“Antes perdia tempo todos os dias a confirmar marcações pelo WhatsApp. Agora o sistema faz isso sozinho e eu só olho para o painel de manhã.”'
                }
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3.5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#1c4f55] text-sm font-semibold text-white">
                  CS
                </span>
                <div>
                  <p className="text-sm font-medium text-[#0f2e32]">Carla Santos</p>
                  <p className="text-xs text-[#5a6e6b]">
                    Studio Carla — Barbearia &amp; Salão
                  </p>
                  <span className="mt-1 inline-block rounded-full bg-[#1c4f55]/[0.08] px-2.5 py-0.5 text-[11px] font-medium text-[#1c4f55]">
                    Ilha Terceira
                  </span>
                </div>
              </figcaption>
            </figure>
          </div>
        </section>

        <div className="mx-auto h-px max-w-[1080px] bg-[#1c4f55]/[0.13]" />

        {/* PROCESS */}
        <section
          id="como-funciona"
          className={cn(WRAP_CLASS_NAME, 'scroll-mt-28 py-16 sm:py-20')}
        >
          <SectionTag>Como arrancas</SectionTag>
          <h2 className="font-display text-[clamp(1.9rem,4vw,2.5rem)] font-bold leading-tight tracking-tight">
            Três passos. Nós fazemos dois.
          </h2>

          <div className="relative mt-11 grid gap-10 md:grid-cols-3 md:gap-0">
            <div
              aria-hidden
              className="absolute left-[16.66%] right-[16.66%] top-[31px] hidden h-px bg-[#1c4f55]/[0.13] md:block"
            />
            {PROCESS_STEPS.map((step, index) => (
              <div
                key={step.title}
                className="relative z-10 text-center md:px-6"
              >
                <span className="mx-auto mb-5 flex h-[62px] w-[62px] items-center justify-center rounded-full bg-[#1c4f55] font-display text-[1.4rem] font-bold text-white">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="mb-2 text-[15px] font-medium text-[#0f2e32]">
                  {step.title}
                </h3>
                <p className="text-[13px] leading-relaxed text-[#5a6e6b]">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* PRICING */}
        <section
          id="precos"
          className={cn(WRAP_CLASS_NAME, 'scroll-mt-28 pb-16 sm:pb-20')}
        >
          <SectionTag>Oferta de entrada</SectionTag>
          <h2 className="font-display text-[clamp(1.9rem,4vw,2.5rem)] font-bold leading-tight tracking-tight">
            Preço simples. Sem surpresas.
          </h2>

          <div className="mt-11 grid gap-10 rounded-[1.5rem] bg-[#1c4f55] p-8 sm:p-12 lg:grid-cols-2">
            <div>
              <p className="text-[13px] text-white/40">A partir de</p>
              <p className="font-display text-[4rem] font-bold leading-none text-[#fad37a]">
                29€
              </p>
              <p className="mt-1 text-sm text-white/45">
                /mês · setup incluído · sem contrato anual
              </p>

              <div className="mt-6 rounded-[0.9rem] border border-[#f5b731]/[0.28] bg-[#f5b731]/[0.12] p-4">
                <p className="mb-1.5 flex items-center gap-1.5 text-[13px] font-medium text-[#fad37a]">
                  <ShieldCheck className="h-4 w-4" />
                  Garantia de resultado — sem risco
                </p>
                <p className="text-xs leading-relaxed text-white/50">
                  Ativamos os lembretes e garantimos que o sistema se paga no
                  primeiro mês. Se não evitarmos pelo menos uma falta no valor da
                  tua mensalidade, o segundo mês é grátis — sem perguntas.
                </p>
              </div>
            </div>

            <div>
              <ul className="mb-7">
                {PRICE_FEATURES.map((feature, index) => (
                  <li
                    key={feature}
                    className={cn(
                      'flex items-center gap-3 py-2.5 text-sm text-white/[0.78]',
                      index < PRICE_FEATURES.length - 1 &&
                        'border-b border-white/[0.08]'
                    )}
                  >
                    <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#f5b731] text-[#0f2e32]">
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>
              <Button
                type="button"
                onClick={handlePrimaryAuditClick}
                size="lg"
                className="h-auto min-h-14 w-full whitespace-normal py-3 leading-snug bg-[#f5b731] text-[#0f2e32] hover:bg-[#f0ad1f]"
              >
                Começar com auditoria gratuita
                <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
              </Button>
            </div>
          </div>
        </section>

        {/* LOCAL */}
        <section className={cn(WRAP_CLASS_NAME, 'pb-16 sm:pb-20')}>
          <div className="grid gap-10 rounded-[1.5rem] border border-[#1c4f55]/[0.13] bg-white p-8 sm:p-12 lg:grid-cols-2">
            <div>
              <SectionTag>Vantagem local</SectionTag>
              <h2 className="font-display text-[clamp(1.7rem,3.5vw,2rem)] font-bold leading-tight tracking-tight">
                Nos Açores, proximidade conta.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-[#5a6e6b]">
                Há ferramentas no mercado. O que muda aqui é o setup feito por nós,
                o acompanhamento humano e o contexto real do mercado açoriano.
                Sabemos como os donos de salão falam, vendem e operam no dia a dia.
              </p>
              <div className="mt-7 flex flex-wrap gap-2.5">
                {LOCAL_BADGES.map((badge) => (
                  <span
                    key={badge}
                    className="rounded-full border border-[#1c4f55]/15 bg-[#1c4f55]/[0.07] px-4 py-2 text-[13px] font-medium text-[#1c4f55]"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-[1.1rem] bg-[#f1eadd] p-6">
              <p className="mb-3.5 flex items-center gap-2 text-xs text-[#5a6e6b]">
                <MapPin className="h-4 w-4 text-[#1c4f55]" />
                Salões que acompanhamos — Açores
              </p>
              <div className="flex flex-wrap gap-2">
                {ISLANDS.map((island) => {
                  const isActive = ACTIVE_ISLANDS.has(island);

                  return (
                    <span
                      key={island}
                      className={cn(
                        'rounded-full px-3.5 py-1.5 text-xs',
                        isActive
                          ? 'bg-[#1c4f55] font-medium text-white/90'
                          : 'bg-[#1c4f55]/10 text-[#1c4f55]'
                      )}
                    >
                      {island}
                    </span>
                  );
                })}
              </div>
              <p className="mt-4 text-xs text-[#5a6e6b]">
                Em expansão para todas as ilhas.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className={cn(WRAP_CLASS_NAME, 'pb-12')}>
          <SectionTag>FAQ</SectionTag>
          <h2 className="font-display text-[clamp(1.9rem,4vw,2.5rem)] font-bold leading-tight tracking-tight">
            Respostas antes de falar connosco.
          </h2>

          <div className="mt-9">
            {LANDING_FAQ_ENTRIES.map((entry) => (
              <details
                key={entry.question}
                className="group border-b border-[#1c4f55]/[0.13]"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-[15px] font-medium text-[#0f2e32] transition-colors hover:text-[#1c4f55] [&::-webkit-details-marker]:hidden">
                  {entry.question}
                  <Plus className="h-5 w-5 shrink-0 text-[#1c4f55] transition-transform duration-200 group-open:rotate-45" />
                </summary>
                <p className="max-w-[680px] pb-5 text-sm leading-[1.75] text-[#5a6e6b]">
                  {entry.answer}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className={cn(WRAP_CLASS_NAME, 'pb-16')}>
          <div className="rounded-[1.75rem] bg-[#1c4f55] p-10 text-center sm:p-16">
            <h2 className="font-display text-[clamp(1.9rem,4.5vw,2.6rem)] font-bold leading-tight tracking-tight text-white">
              O primeiro mês paga-se a si próprio.
            </h2>
            <p className="mx-auto mt-4 max-w-[34rem] text-base leading-relaxed text-white/50">
              Ativamos os lembretes em 48h. Se não evitarmos pelo menos uma falta
              no valor da mensalidade, o segundo mês é grátis.
            </p>
            <div className="mt-9 flex flex-col justify-center gap-3.5 sm:flex-row sm:flex-wrap">
              <Button
                asChild
                size="lg"
                className="h-auto min-h-14 w-full whitespace-normal py-3 leading-snug bg-[#f5b731] text-[#0f2e32] shadow-[0_6px_20px_rgba(245,183,49,0.35)] hover:bg-[#f0ad1f] sm:w-auto"
              >
                <a href="#auditoria" onClick={handlePrimaryAuditClick}>
                  Pedir auditoria gratuita
                  <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-auto min-h-14 w-full whitespace-normal py-3 leading-snug border-white/20 bg-transparent text-white/80 hover:border-white/45 hover:bg-white/5 hover:text-white sm:w-auto"
              >
                <a href="#calculadora" onClick={handleCalculatorNavigationClick}>
                  Calcular o que perco
                </a>
              </Button>
            </div>
            <p className="mt-5 text-xs text-white/30">
              Desde 29€/mês · Setup em 48h · Paga-se no 1.º mês ou 2.º mês grátis
            </p>
          </div>
        </section>

        {/* AUDIT FORM */}
        <section
          id="auditoria"
          className={cn(WRAP_CLASS_NAME, 'scroll-mt-28 pb-16 sm:pb-20')}
        >
          <div className="grid gap-6 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]">
            <div className="overflow-hidden rounded-[1.5rem] bg-[#1c4f55] p-8 text-white sm:p-10">
              <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.2em] text-[#f1eadd]">
                Pedir auditoria gratuita
              </span>
              <h2 className="mt-5 font-display text-[clamp(1.7rem,3.5vw,2.1rem)] font-bold leading-tight tracking-tight">
                Começamos por uma auditoria simples ao teu caso.
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-white/60">
                O objetivo é perceber onde estás a perder tempo ou faturação e se o
                BelaHora faz sentido para a fase em que o teu salão está.
              </p>

              <div className="mt-7 space-y-3">
                {[
                  { icon: CalendarClock, title: 'Videochamada de 30 minutos incluída' },
                  { icon: Clock3, title: 'Resposta manual, sem funis automáticos frios' },
                  {
                    icon: CircleAlert,
                    title: 'Sem compromisso de compra para pedir a leitura inicial',
                  },
                ].map(({ icon: Icon, title }) => (
                  <div
                    key={title}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.08] px-4 py-3"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f5b731] text-[#0f2e32]">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-medium text-white/90">{title}</span>
                  </div>
                ))}
              </div>

              {auditContext.estimatedMonthlyLossEur !== null ? (
                <div className="mt-6 rounded-xl border border-[#f5b731]/30 bg-[#f5b731]/[0.12] px-5 py-4 text-sm leading-relaxed text-[#fad37a]">
                  Vamos receber este pedido com uma perda mensal estimada de{' '}
                  <span className="font-semibold text-white">
                    {formatCurrency(auditContext.estimatedMonthlyLossEur)}
                  </span>
                  .
                </div>
              ) : null}
            </div>

            <div className="rounded-[1.5rem] border border-[#1c4f55]/[0.13] bg-white p-8 sm:p-10">
              <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="space-y-2">
                  <p className="font-display text-[1.65rem] font-bold text-[#0f2e32]">
                    Recebe uma leitura inicial do teu caso.
                  </p>
                  <p className="text-sm leading-relaxed text-[#5a6e6b]">
                    Respondemos com base no contexto do salão, sem sequências
                    automáticas nem conversa de script.
                  </p>
                </div>

                {submissionState === 'success' ? (
                  <div
                    role="status"
                    className="rounded-xl border border-[#1c4f55]/20 bg-[#1c4f55]/[0.06] px-4 py-4 text-sm leading-relaxed text-[#1c4f55]"
                  >
                    Pedido enviado. Vamos analisar o contexto e responder com os
                    próximos passos da auditoria gratuita.
                  </div>
                ) : null}

                {submissionState === 'error' ? (
                  <div
                    role="alert"
                    className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-4 text-sm font-medium text-destructive"
                  >
                    {submissionError ?? 'Não foi possível enviar o pedido agora.'}
                  </div>
                ) : null}

                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="space-y-2">
                    <span className={LABEL_CLASS_NAME}>Nome</span>
                    <Input
                      {...register('ownerName')}
                      aria-describedby={errors.ownerName ? 'owner-name-error' : undefined}
                      className={FIELD_CLASS_NAME}
                      placeholder="Ex: Ana Silva"
                    />
                    <FieldMessage id="owner-name-error" message={errors.ownerName?.message} />
                  </label>

                  <label className="space-y-2">
                    <span className={LABEL_CLASS_NAME}>Nome do salão</span>
                    <Input
                      {...register('businessName')}
                      aria-describedby={
                        errors.businessName ? 'business-name-error' : undefined
                      }
                      className={FIELD_CLASS_NAME}
                      placeholder="Ex: Studio Ana"
                    />
                    <FieldMessage
                      id="business-name-error"
                      message={errors.businessName?.message}
                    />
                  </label>

                  <div className="space-y-2">
                    <label className={LABEL_CLASS_NAME} htmlFor="phone-or-whatsapp">
                      Telefone ou WhatsApp
                    </label>
                    <Controller
                      control={control}
                      name="phoneOrWhatsapp"
                      render={({ field }) => (
                        <PhoneInput
                          id="phone-or-whatsapp"
                          label="Telefone ou WhatsApp"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          inputClassName={FIELD_CLASS_NAME}
                          selectClassName={FIELD_CLASS_NAME}
                          placeholder="912 345 678"
                          aria-describedby={
                            errors.phoneOrWhatsapp ? 'phone-or-whatsapp-error' : undefined
                          }
                          aria-invalid={Boolean(errors.phoneOrWhatsapp)}
                        />
                      )}
                    />
                    <FieldMessage
                      id="phone-or-whatsapp-error"
                      message={errors.phoneOrWhatsapp?.message}
                    />
                  </div>

                  <label className="space-y-2">
                    <span className={LABEL_CLASS_NAME}>Ilha</span>
                    <SelectField
                      {...register('island')}
                      id="island"
                      describedBy={errors.island ? 'island-error' : undefined}
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Escolhe a ilha
                      </option>
                      {ISLANDS.map((island) => (
                        <option key={island} value={island}>
                          {island}
                        </option>
                      ))}
                    </SelectField>
                    <FieldMessage id="island-error" message={errors.island?.message} />
                  </label>

                  <label className="space-y-2">
                    <span className={LABEL_CLASS_NAME}>Email (opcional)</span>
                    <Input
                      {...register('email')}
                      type="email"
                      aria-describedby={errors.email ? 'email-error' : undefined}
                      className={FIELD_CLASS_NAME}
                      placeholder="ana@exemplo.pt"
                    />
                    <FieldMessage id="email-error" message={errors.email?.message} />
                  </label>

                  <label className="space-y-2">
                    <span className={LABEL_CLASS_NAME}>Instagram (opcional)</span>
                    <Input
                      {...register('instagramHandle')}
                      aria-describedby={
                        errors.instagramHandle ? 'instagram-handle-error' : undefined
                      }
                      className={FIELD_CLASS_NAME}
                      placeholder="@studioana"
                    />
                    <FieldMessage
                      id="instagram-handle-error"
                      message={errors.instagramHandle?.message}
                    />
                  </label>
                </div>

                <div className="rounded-xl border border-[#f5b731]/40 bg-[#f5b731]/[0.12] px-4 py-4 text-sm leading-relaxed text-[#8a5a13]">
                  Este consentimento serve apenas para responder ao teu pedido de
                  auditoria. Campanhas de marketing exigem consentimento próprio e
                  separado.
                </div>

                <Controller
                  control={control}
                  name="consent"
                  render={({ field }) => (
                    <div className="space-y-2">
                      <label className="flex items-start gap-3 rounded-xl border border-[#1c4f55]/15 bg-[#f1eadd]/60 px-4 py-4">
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked === true)}
                          className="mt-1 border-[#1c4f55]/40 data-[state=checked]:border-[#1c4f55] data-[state=checked]:bg-[#1c4f55]"
                        />
                        <span className="text-sm leading-relaxed text-[#0f2e32]">
                          Autorizo a BelaHora a contactar-me para responder a este
                          pedido de auditoria gratuita.
                        </span>
                      </label>
                      <FieldMessage id="consent-error" message={errors.consent?.message} />
                    </div>
                  )}
                />

                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm leading-relaxed text-[#5a6e6b]">
                    Se preferires, também podes falar connosco depois da auditoria
                    para perceber qual é o plano certo para o teu salão.
                  </p>
                  <Button
                    type="submit"
                    disabled={submissionState === 'submitting'}
                    size="lg"
                    className="h-auto min-h-14 w-full shrink-0 whitespace-normal py-3 leading-snug bg-[#1c4f55] hover:bg-[#163f44] sm:w-auto"
                  >
                    {submissionState === 'submitting'
                      ? 'A enviar pedido...'
                      : 'Pedir auditoria gratuita'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#1c4f55]/[0.13] py-7">
        <div
          className={cn(
            WRAP_CLASS_NAME,
            'flex flex-col items-center justify-between gap-3 sm:flex-row'
          )}
        >
          <span className="text-xs text-[#5a6e6b]">
            © {new Date().getFullYear()} BelaHora — Marcações para salões nos Açores
          </span>
          <nav
            aria-label="Links legais"
            className="flex flex-wrap items-center justify-center gap-5"
          >
            <Link
              to="/dpa"
              className="text-xs text-[#5a6e6b] transition-colors hover:text-[#1c4f55]"
            >
              DPA
            </Link>
            {LEGAL_LINKS.map(({ href, label }) => (
              <Link
                key={href}
                to={href}
                className="text-xs text-[#5a6e6b] transition-colors hover:text-[#1c4f55]"
              >
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}

export { calculateLosses };
